import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Activity, RotateCcw, Trash2, Plus, X, Dumbbell, Heart, Footprints, Timer, Flame, Camera, Loader2, Sparkles } from "lucide-react";
import BodyDiagram, { MUSCLE_LABELS } from "@/components/BodyDiagram";
import LogCalendar from "@/components/LogCalendar";
import { CameraCapture } from "@/components/CameraCapture";
import useUserSync from "@/hooks/useUserSync";
import { useAuth } from "@/contexts/AuthContext";

const STORAGE_KEY = "omnimuscle_log_v1";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------------- Exercise library ---------------- */
const EXERCISES = {
  chest: ["Bench Press", "Incline DB Press", "Cable Fly", "Push-Up", "Dips", "Decline Press", "Pec Deck"],
  shoulders: ["Overhead Press", "Lateral Raise", "Front Raise", "Arnold Press", "Upright Row", "Face Pull"],
  biceps: ["Barbell Curl", "DB Curl", "Hammer Curl", "Preacher Curl", "Cable Curl", "Concentration Curl"],
  forearms: ["Wrist Curl", "Reverse Curl", "Farmer Carry", "Plate Pinch"],
  abs: ["Crunch", "Hanging Leg Raise", "Plank", "Cable Crunch", "Russian Twist", "Ab Wheel"],
  quads: ["Back Squat", "Front Squat", "Leg Press", "Walking Lunge", "Leg Extension", "Bulgarian Split"],
  calves: ["Standing Calf Raise", "Seated Calf Raise", "Donkey Calf Raise", "Single-Leg Calf"],
  traps: ["Barbell Shrug", "DB Shrug", "Rack Pull", "Farmer Carry"],
  lats: ["Pull-Up", "Lat Pulldown", "Bent-Over Row", "Cable Row", "T-Bar Row", "Pullover"],
  triceps: ["Skull Crusher", "Tricep Pushdown", "Close-Grip Bench", "Overhead Extension", "Dips", "Diamond Push-Up"],
  "lower-back": ["Deadlift", "Hyperextension", "Good Morning", "Romanian DL"],
  glutes: ["Hip Thrust", "Glute Bridge", "Romanian DL", "Cable Kickback", "Sumo Squat", "Bulgarian Split"],
  hamstrings: ["Romanian DL", "Leg Curl", "Stiff-Leg DL", "Glute-Ham Raise", "Nordic Curl"],
};

/* ---------------- Cardio library ---------------- */
const CARDIO_TYPES = [
  { key: "run",   label: "Run",        icon: "🏃", hasDistance: true },
  { key: "walk",  label: "Walk",       icon: "🚶", hasDistance: true },
  { key: "bike",  label: "Bike",       icon: "🚴", hasDistance: true },
  { key: "row",   label: "Row",        icon: "🚣", hasDistance: true },
  { key: "hiit",  label: "HIIT",       icon: "🔥", hasDistance: false },
  { key: "stair", label: "Stair",      icon: "🪜", hasDistance: false },
  { key: "swim",  label: "Swim",       icon: "🏊", hasDistance: true },
  { key: "other", label: "Other",      icon: "❤️", hasDistance: true },
];

/* Heat levels */
function heatColor(count) {
  if (count === 0) return "#1F1F1F";
  if (count === 1) return "#1E3A8A";
  if (count === 2) return "#16A34A";
  if (count === 3) return "#D4AF37";
  return "#DC2626"; // 4+
}

function todayKey() {
  // Use Pacific Time date so "today" rolls over at midnight PT, not UTC.
  // Returns YYYY-MM-DD as observed in America/Los_Angeles.
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const d = parts.find((p) => p.type === "day")?.value;
  return `${y}-${m}-${d}`;
}

// Returns the YYYY-MM-DD (Pacific Time) of the most recent Monday — i.e. the
// start of the current PT calendar week. Used to scope "weekly" stats so the
// heatmap resets every Monday 00:00 PT, not on a rolling 7-day basis.
function mondayKey() {
  // Get today's PT date components
  const now = new Date();
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
  });
  const parts = fmt.formatToParts(now);
  const wk = parts.find((p) => p.type === "weekday")?.value; // Sun..Sat
  const y = Number(parts.find((p) => p.type === "year")?.value);
  const m = Number(parts.find((p) => p.type === "month")?.value);
  const d = Number(parts.find((p) => p.type === "day")?.value);
  const wkMap = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
  const daysSinceMonday = wkMap[wk] ?? 0;
  // Build a UTC date for the PT calendar day, then subtract the offset.
  // Anchor at UTC noon to avoid TZ edge issues.
  const anchor = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  anchor.setUTCDate(anchor.getUTCDate() - daysSinceMonday);
  const yy = anchor.getUTCFullYear();
  const mm = String(anchor.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(anchor.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

// True when the entry's date (YYYY-MM-DD, Pacific) is on/after this week's Monday.
function withinThisWeek(dateStr) {
  if (!dateStr) return false;
  return String(dateStr) >= mondayKey(); // ISO date strings are lexicographically comparable
}

/* ---------------- Main component ---------------- */
export default function MuscleTracker() {
  const { logActivity } = useAuth();
  const [mode, setMode] = useState("strength"); // strength | cardio | heatmap
  const [gender, setGender] = useState("male");
  const [view, setView] = useState("front");
  const [selectedMuscle, setSelectedMuscle] = useState(null);
  const [customExercise, setCustomExercise] = useState("");
  const [hover, setHover] = useState(null);
  const [log, setLog] = useState([]);
  // 📅 Shared calendar — selected date + panel toggle.
  const [selectedDate, setSelectedDate] = useState(todayKey());
  const [showCalendar, setShowCalendar] = useState(false);
  // 📸 Gym Equipment Cam — snap → AI-identify → suggest exercises → auto-pick muscle
  const [camOpen, setCamOpen] = useState(false);
  const [camScanning, setCamScanning] = useState(false);
  const [camResult, setCamResult] = useState(null); // { detected, exercises, fallback }

  // Load from storage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setLog(JSON.parse(raw));
    } catch {
      // ignore
    }
  }, []);

  // Save on change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(log));
    } catch {
      // ignore
    }
  }, [log]);

  // Re-render every minute so the PT day/week boundary auto-rolls
  // without the user needing to refresh the page near midnight PT.
  // eslint-disable-next-line no-unused-vars
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // Cross-device sync (no-op when logged out)
  useUserSync({
    scope: "workouts",
    readLocal: () => ({ log }),
    writeLocal: (merged) => {
      if (Array.isArray(merged?.log)) setLog(merged.log);
    },
    merge: (server, local) => {
      const sLog = Array.isArray(server?.log) ? server.log : [];
      const lLog = Array.isArray(local?.log) ? local.log : [];
      const seen = new Set();
      const out = [];
      for (const e of [...sLog, ...lLog]) {
        if (!e) continue;
        const key = e.id || `${e.date}|${e.type || "strength"}|${e.muscle || e.cardio_type || ""}|${e.name || ""}|${e.sets || ""}|${e.reps || ""}|${e.weight || ""}|${e.duration_min || ""}|${e.distance || ""}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(e);
      }
      return { log: out };
    },
    deps: [log],
  });

  // Weekly counts per muscle — only count strength entries
  const weekly = useMemo(() => {
    const counts = {};
    log
      .filter((e) => withinThisWeek(e.date) && (!e.type || e.type === "strength"))
      .forEach((e) => {
        counts[e.muscle] = (counts[e.muscle] || 0) + 1;
      });
    return counts;
  }, [log]);

  // Weekly cardio totals
  const cardioWeek = useMemo(() => {
    const entries = log.filter((e) => withinThisWeek(e.date) && e.type === "cardio");
    const totalMin = entries.reduce((s, e) => s + (Number(e.duration_min) || 0), 0);
    const totalDist = entries.reduce((s, e) => s + (Number(e.distance) || 0), 0);
    const totalSteps = entries.reduce((s, e) => s + (Number(e.steps) || 0), 0);
    const totalCal = entries.reduce((s, e) => s + (Number(e.calories) || 0), 0);
    return { count: entries.length, totalMin, totalDist, totalSteps, totalCal };
  }, [log]);

  const today = todayKey();
  // 📅 View log filtered by the shared calendar's selected date (default = today).
  const todayLog = log.filter((e) => e.date === selectedDate);
  const todayStrength = todayLog.filter((e) => !e.type || e.type === "strength");
  const todayCardio = todayLog.filter((e) => e.type === "cardio");
  const isViewingToday = selectedDate === today;
  // 📅 Cross-stream dots — mixes MuscleTracker (gym/cardio) + FoodLog entries.
  const dotsByDate = useMemo(() => {
    const dots = {};
    (log || []).forEach((e) => {
      if (!e.date) return;
      const arr = dots[e.date] || [];
      const tag = e.type === "cardio" ? "cardio" : "gym";
      if (!arr.includes(tag)) arr.push(tag);
      dots[e.date] = arr;
    });
    try {
      const foodRaw = localStorage.getItem("omnimuscle_food_log_v1");
      if (foodRaw) {
        const foodLog = JSON.parse(foodRaw);
        foodLog.forEach((e) => {
          if (!e.date) return;
          const arr = dots[e.date] || [];
          if (!arr.includes("food")) arr.push("food");
          dots[e.date] = arr;
        });
      }
    } catch (_e) { /* ignore */ }
    return dots;
  }, [log]);

  const addExercise = (muscle, name, sets, reps, weight, unit, duration_min, avg_bpm) => {
    const entry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type: "strength",
      date: selectedDate,   // 📅 calendar-selected (retroactive/future OK)
      muscle,
      name,
      sets: sets ? Number(sets) : null,
      reps: reps ? Number(reps) : null,
      weight: weight ? Number(weight) : null,
      unit: unit || "lb",
      duration_min: duration_min ? Number(duration_min) : null,
      avg_bpm: avg_bpm ? Number(avg_bpm) : null,
    };
    setLog((l) => [...l, entry]);
    logActivity?.("workout");
  };

  const addCardio = ({ cardio_type, label, duration_min, distance, distance_unit, steps, avg_bpm, calories }) => {
    const entry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type: "cardio",
      date: selectedDate,   // 📅 calendar-selected (retroactive/future OK)
      cardio_type,
      name: label,
      duration_min: duration_min ? Number(duration_min) : null,
      distance: distance ? Number(distance) : null,
      distance_unit: distance_unit || "mi",
      steps: steps ? Number(steps) : null,
      avg_bpm: avg_bpm ? Number(avg_bpm) : null,
      calories: calories ? Number(calories) : null,
    };
    setLog((l) => [...l, entry]);
    logActivity?.("cardio");
  };

  const removeEntry = (id) => setLog((l) => l.filter((e) => e.id !== id));

  // 📸 Camera capture → send to backend → open suggestions.
  const onCamCapture = async (dataUrl) => {
    setCamOpen(false);
    if (!dataUrl) return;
    setCamScanning(true);
    setCamResult(null);
    try {
      const r = await axios.post(`${API}/gym/exercise-from-photo`, { photo_b64: dataUrl });
      const data = r.data || {};
      setCamResult(data);
      const conf = Math.round((data?.detected?.confidence || 0) * 100);
      if (data?.detected?.name) {
        toast.success(`📸 Detected: ${data.detected.name} (${conf}%)`);
      } else if (data?.fallback) {
        toast(`📸 Couldn't ID the machine — pick from the full catalog.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed. Try again.");
    } finally {
      setCamScanning(false);
    }
  };

  // Called when the user taps a suggested exercise in the modal.
  // Auto-selects the primary muscle, sets mode to Strength, and pre-fills
  // the custom exercise input so the user only enters sets/reps.
  const onPickSuggestion = (sug) => {
    const m = sug.primary_tracker_muscle || (sug.tracker_muscles || [])[0];
    if (m) {
      setMode("strength");
      setSelectedMuscle(m);
      setCustomExercise(sug.name || "");
      // Scroll into view so the picker is visible.
      setTimeout(() => {
        document.querySelector('[data-testid="exercise-picker"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
      toast.success(`Ready to log: ${sug.name} → ${MUSCLE_LABELS[m] || m}`);
    } else {
      toast.error("Couldn't map that exercise to a muscle group.");
    }
    setCamResult(null);
  };
  const clearToday = () => {
    if (!window.confirm("Clear all of today's logged exercises?")) return;
    setLog((l) => l.filter((e) => e.date !== todayKey()));
  };
  const resetWeek = () => {
    if (!window.confirm("Reset the entire weekly log? This cannot be undone."))
      return;
    setLog([]);
  };

  return (
    <section
      id="tracker"
      data-testid="muscle-tracker-section"
      className="py-24 md:py-36 bg-[#080808]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="text-[#D4AF37] font-accent text-xs uppercase tracking-[0.3em] mb-3">
            Visual Workout Tracker
          </div>
          <h2 className="font-display text-5xl md:text-7xl text-white leading-[0.9] mb-4">
            Map The <span className="text-[#DC2626]">Work.</span>
          </h2>
          <p className="text-neutral-400 max-w-2xl mx-auto">
            Click any muscle on the diagram. Log exercises with sets, reps, and
            weight. Watch your weekly heat-map fill up in real time. <span className="text-[#D4AF37]">Today resets at 00:00 PT · Weekly resets every Monday 00:00 PT.</span>
          </p>
        </div>

        {/* 📅 Shared calendar — retroactive + future workout logging.
            Cross-links Gym / Cardio / Food dots so you can see everything on one day. */}
        <div className="mb-6 flex items-start gap-3 flex-col sm:flex-row sm:items-center sm:justify-center">
          <button
            onClick={() => setShowCalendar((v) => !v)}
            className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-neutral-300 hover:text-[#D4AF37] px-3 py-2 border border-zinc-800 hover:border-[#D4AF37] transition-colors"
            data-testid="muscle-calendar-toggle"
          >
            📅 {showCalendar ? "Hide Calendar" : "Calendar"}
            {!isViewingToday && (
              <span className="text-[#D4AF37] normal-case tracking-normal font-normal">
                · {selectedDate}
              </span>
            )}
          </button>
          {!isViewingToday && (
            <button
              onClick={() => setSelectedDate(today)}
              className="text-[10px] uppercase tracking-widest font-mono text-neutral-400 hover:text-[#D4AF37]"
              data-testid="muscle-calendar-jump-today"
            >
              ← Back to Today
            </button>
          )}
          {/* 📸 Gym Equipment Cam — AI-assisted logging trigger */}
          <button
            onClick={() => setCamOpen(true)}
            disabled={camScanning}
            className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-black bg-[#D4AF37] hover:bg-[#f0c447] disabled:opacity-60 px-3 py-2 border-2 border-[#D4AF37] transition-colors"
            data-testid="muscle-equipment-cam-btn"
            title="Snap gym equipment to auto-suggest exercises"
          >
            {camScanning ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />}
            {camScanning ? "Scanning…" : "Log via Camera"}
          </button>
        </div>
        {showCalendar && (
          <div className="mb-6 max-w-md mx-auto">
            <LogCalendar
              selectedDate={selectedDate}
              onSelectDate={(d) => setSelectedDate(d)}
              dotsByDate={dotsByDate}
              streams={["food", "gym", "cardio"]}
            />
          </div>
        )}

        {/* Mode tabs: Strength | Cardio | Heatmap */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
          <div className="flex border-2 border-zinc-800">
            {[
              { k: "strength", label: "Strength", icon: <Dumbbell size={12} /> },
              { k: "cardio", label: "Cardio", icon: <Heart size={12} /> },
              { k: "heatmap", label: "Heatmap", icon: <Activity size={12} /> },
            ].map((m) => (
              <button
                key={m.k}
                data-testid={`tracker-mode-${m.k}`}
                onClick={() => setMode(m.k)}
                className={`px-5 py-2 font-accent uppercase text-xs tracking-widest font-bold transition-colors flex items-center gap-2 ${
                  mode === m.k
                    ? "bg-[#D4AF37] text-black"
                    : "text-neutral-300 hover:text-white"
                }`}
              >
                {m.icon}
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {/* Gender + View toggles (only relevant for Strength + Heatmap) */}
        {mode !== "cardio" && (
        <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
          <div className="flex border-2 border-zinc-800">
            {["male", "female"].map((g) => (
              <button
                key={g}
                data-testid={`tracker-gender-${g}`}
                onClick={() => setGender(g)}
                className={`px-5 py-2 font-accent uppercase text-xs tracking-widest font-bold transition-colors ${
                  gender === g
                    ? "bg-[#D4AF37] text-black"
                    : "text-neutral-300 hover:text-white"
                }`}
              >
                {g}
              </button>
            ))}
          </div>
          <div className="flex border-2 border-zinc-800">
            {["front", "back"].map((v) => (
              <button
                key={v}
                data-testid={`tracker-view-${v}`}
                onClick={() => setView(v)}
                className={`px-5 py-2 font-accent uppercase text-xs tracking-widest font-bold transition-colors ${
                  view === v
                    ? "bg-[#D4AF37] text-black"
                    : "text-neutral-300 hover:text-white"
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
        )}

        {mode === "strength" && (
        <div className="grid lg:grid-cols-12 gap-6">
          {/* Body diagram */}
          <div className="lg:col-span-5 bg-[#0F0F0F] border border-zinc-800 p-6">
            <div className="max-w-xs mx-auto">
              <BodyDiagram
                gender={gender}
                view={view}
                weekly={weekly}
                onPick={setSelectedMuscle}
                hover={hover}
                setHover={setHover}
                heatColor={heatColor}
              />
            </div>

            {/* Heat-map legend */}
            <div className="mt-4 pt-4 border-t border-zinc-800">
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">
                This Week's Heat <span className="text-neutral-700 normal-case tracking-wider">· Resets Mon 00:00 PT</span>
              </div>
              <div className="flex items-center gap-1 text-[10px]">
                {[
                  { c: "#1F1F1F", l: "0" },
                  { c: "#1E3A8A", l: "1" },
                  { c: "#16A34A", l: "2" },
                  { c: "#D4AF37", l: "3" },
                  { c: "#DC2626", l: "4+" },
                ].map((s) => (
                  <div key={s.l} className="flex items-center gap-1.5 mr-2">
                    <span
                      className="inline-block w-4 h-4"
                      style={{ background: s.c, border: "1px solid #333" }}
                    />
                    <span className="text-neutral-400">{s.l}x</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Exercise picker */}
          <div className="lg:col-span-4 bg-[#0F0F0F] border border-zinc-800 p-6">
            {selectedMuscle ? (
              <ExercisePicker
                muscle={selectedMuscle}
                customExercise={customExercise}
                setCustomExercise={setCustomExercise}
                onAdd={(name, sets, reps, weight, unit, duration_min, avg_bpm) =>
                  addExercise(selectedMuscle, name, sets, reps, weight, unit, duration_min, avg_bpm)
                }
                onClose={() => setSelectedMuscle(null)}
              />
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center min-h-[300px]">
                <Activity size={48} className="text-[#D4AF37] mb-4" />
                <h3 className="font-display text-2xl text-white mb-2">
                  Tap A Muscle
                </h3>
                <p className="text-neutral-400 text-sm max-w-xs">
                  Click any muscle on the diagram to see exercises and log your
                  work.
                </p>
              </div>
            )}
          </div>

          {/* Today's log (strength) */}
          <div className="lg:col-span-3 bg-[#0F0F0F] border border-zinc-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
                  Today's Workout
                </div>
                <div className="font-display text-2xl text-white">
                  {todayStrength.length} {todayStrength.length === 1 ? "Set" : "Sets"}
                </div>
              </div>
            </div>

            <div className="space-y-2 mb-4 max-h-[420px] overflow-y-auto pr-1">
              {todayStrength.length === 0 ? (
                <p className="text-neutral-500 text-sm py-8 text-center">
                  Nothing logged yet. Pick a muscle to begin.
                </p>
              ) : (
                todayStrength.map((e) => (
                  <div
                    key={e.id}
                    data-testid={`log-entry-${e.id}`}
                    className="bg-[#0A0A0A] border border-zinc-800 p-3 group"
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="font-display text-sm text-white leading-tight">
                        {e.name}
                      </div>
                      <button
                        data-testid={`log-remove-${e.id}`}
                        onClick={() => removeEntry(e.id)}
                        className="text-neutral-600 hover:text-[#DC2626] opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label="Remove entry"
                      >
                        <X size={14} />
                      </button>
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">
                      {MUSCLE_LABELS[e.muscle]}
                    </div>
                    {(e.sets || e.reps || e.weight) && (
                      <div className="text-xs text-neutral-400 mt-1">
                        {e.sets ? `${e.sets} × ` : ""}
                        {e.reps || "—"} reps
                        {e.weight ? ` @ ${e.weight} ${e.unit}` : ""}
                      </div>
                    )}
                    {(e.duration_min || e.avg_bpm) && (
                      <div className="text-[11px] text-neutral-500 mt-1 flex items-center gap-3">
                        {e.duration_min ? (
                          <span className="flex items-center gap-1"><Timer size={10} className="text-[#D4AF37]" />{e.duration_min}m</span>
                        ) : null}
                        {e.avg_bpm ? (
                          <span className="flex items-center gap-1"><Heart size={10} className="text-[#DC2626]" />{e.avg_bpm} BPM</span>
                        ) : null}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="space-y-2 pt-4 border-t border-zinc-800">
              <button
                data-testid="tracker-clear-today"
                onClick={clearToday}
                disabled={todayLog.length === 0}
                className="w-full text-xs uppercase tracking-widest font-bold text-neutral-400 hover:text-[#DC2626] py-2 border border-zinc-800 hover:border-[#DC2626] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <Trash2 size={12} className="inline mr-2" />
                Clear Today
              </button>
              <button
                data-testid="tracker-reset-week"
                onClick={resetWeek}
                disabled={log.length === 0}
                className="w-full text-xs uppercase tracking-widest font-bold text-neutral-400 hover:text-[#DC2626] py-2 border border-zinc-800 hover:border-[#DC2626] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <RotateCcw size={12} className="inline mr-2" />
                Reset Week
              </button>
            </div>
          </div>
        </div>
        )}

        {mode === "cardio" && (
          <CardioPanel
            log={log}
            todayCardio={todayCardio}
            cardioWeek={cardioWeek}
            onAdd={addCardio}
            onRemove={removeEntry}
            onClearToday={clearToday}
          />
        )}

        {mode === "heatmap" && (
          <div className="grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7 bg-[#0F0F0F] border border-zinc-800 p-8">
              <div className="max-w-md mx-auto">
                <BodyDiagram
                  gender={gender}
                  view={view}
                  weekly={weekly}
                  onPick={() => {}}
                  hover={hover}
                  setHover={setHover}
                  heatColor={heatColor}
                />
              </div>
            </div>
            <div className="lg:col-span-5 bg-[#0F0F0F] border border-zinc-800 p-6 space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1">
                  This Week
                </div>
                <div className="font-display text-3xl text-white">
                  {Object.values(weekly).reduce((a, b) => a + b, 0)} Sets · {cardioWeek.count} Cardio
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-[#0A0A0A] border border-zinc-800 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">Time</div>
                  <div className="font-display text-2xl text-[#D4AF37]">{cardioWeek.totalMin}m</div>
                </div>
                <div className="bg-[#0A0A0A] border border-zinc-800 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">Distance</div>
                  <div className="font-display text-2xl text-[#D4AF37]">{cardioWeek.totalDist.toFixed(1)}</div>
                </div>
                <div className="bg-[#0A0A0A] border border-zinc-800 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">Calories</div>
                  <div className="font-display text-2xl text-[#D4AF37]">{cardioWeek.totalCal}</div>
                </div>
              </div>
              <div className="pt-4 border-t border-zinc-800">
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">
                  Muscle Coverage
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.keys(weekly).map((m) => (
                    <span key={m} className="px-2 py-1 text-[10px] uppercase tracking-widest bg-[#0A0A0A] border border-zinc-800 text-neutral-300">
                      {MUSCLE_LABELS[m] || m} <span className="text-[#D4AF37]">×{weekly[m]}</span>
                    </span>
                  ))}
                  {Object.keys(weekly).length === 0 && (
                    <p className="text-neutral-500 text-xs">No muscles trained this week. Switch to Strength to begin.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 📸 Camera modal — snap a piece of gym equipment */}
      <CameraCapture
        open={camOpen}
        title="Snap gym equipment"
        hint="Frame ONE machine or piece of gear clearly"
        onCapture={onCamCapture}
        onClose={() => setCamOpen(false)}
      />

      {/* 🔮 Suggestions overlay — pick a suggested exercise → auto-select muscle + prefill */}
      {camResult && (
        <div
          className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6"
          data-testid="muscle-cam-suggestions"
          onClick={() => setCamResult(null)}
        >
          <div
            className="relative w-full max-w-lg bg-[#0F0F0F] border-2 border-[#D4AF37] shadow-2xl rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-4 border-b border-zinc-800">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold flex items-center gap-1">
                  <Sparkles size={11} /> AI Suggestion
                  <span className="text-neutral-500 normal-case tracking-normal">· {Math.round((camResult?.detected?.confidence || 0) * 100)}%</span>
                </div>
                <div className="font-display text-xl text-white truncate">
                  {camResult?.detected?.name || (camResult?.fallback ? "Full Exercise Catalog" : "Unknown Equipment")}
                </div>
                <div className="text-xs text-neutral-400 mt-1">
                  {camResult?.fallback
                    ? "Couldn't clearly ID the gear — pick any exercise below."
                    : `Pick one to auto-fill the tracker.`}
                </div>
              </div>
              <button
                onClick={() => setCamResult(null)}
                className="p-1.5 rounded bg-black/60 border border-zinc-700 hover:bg-zinc-800"
                aria-label="Close"
                data-testid="muscle-cam-suggestions-close"
              >
                <X size={14} className="text-white" />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto p-3 space-y-2">
              {(camResult?.exercises || []).length === 0 ? (
                <p className="text-center text-neutral-500 text-sm py-8">No exercises to suggest.</p>
              ) : (
                (camResult.exercises || []).map((sug) => (
                  <button
                    key={sug.exercise_id}
                    onClick={() => onPickSuggestion(sug)}
                    disabled={!sug.primary_tracker_muscle}
                    className="w-full text-left bg-[#0A0A0A] border border-zinc-800 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 disabled:opacity-40 transition-colors p-3 group"
                    data-testid={`muscle-cam-suggestion-${sug.exercise_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="text-2xl">{sug.hero_emoji || "🏋️"}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-sm text-white leading-tight">{sug.name}</div>
                        <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mt-0.5">
                          {(sug.tracker_muscles || []).map((m) => MUSCLE_LABELS[m] || m).join(" · ") || "—"}
                        </div>
                        {sug.one_line && (
                          <div className="text-[11px] text-neutral-400 mt-1 line-clamp-2">{sug.one_line}</div>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/* ---------------- ExercisePicker ---------------- */
function ExercisePicker({ muscle, customExercise, setCustomExercise, onAdd, onClose }) {
  const [chosen, setChosen] = useState(null);
  const [sets, setSets] = useState("");
  const [reps, setReps] = useState("");
  const [weight, setWeight] = useState("");
  const [unit, setUnit] = useState("lb");
  const [duration, setDuration] = useState("");
  const [bpm, setBpm] = useState("");

  const exerciseList = EXERCISES[muscle] || [];

  const submit = () => {
    const name = chosen || customExercise.trim();
    if (!name) return;
    onAdd(name, sets, reps, weight, unit, duration, bpm);
    setChosen(null);
    setCustomExercise("");
    setSets("");
    setReps("");
    setWeight("");
    setDuration("");
    setBpm("");
  };

  return (
    <div data-testid="exercise-picker">
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
            Selected
          </div>
          <h3 className="font-display text-3xl text-white leading-tight">
            {MUSCLE_LABELS[muscle]}
          </h3>
        </div>
        <button
          data-testid="exercise-picker-close"
          onClick={onClose}
          className="text-neutral-500 hover:text-white"
          aria-label="Close"
        >
          <X size={20} />
        </button>
      </div>

      {/* Exercise chips */}
      <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold mb-2">
        Pick An Exercise
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {exerciseList.map((ex) => (
          <button
            key={ex}
            data-testid={`exercise-chip-${ex}`}
            onClick={() => {
              setChosen(ex);
              setCustomExercise("");
            }}
            className={`px-3 py-1.5 text-xs font-semibold border-2 transition-colors ${
              chosen === ex
                ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white"
                : "border-zinc-800 text-neutral-300 hover:border-zinc-600"
            }`}
          >
            {ex}
          </button>
        ))}
      </div>

      {/* Custom exercise */}
      <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold mb-2">
        Or Add Your Own
      </div>
      <input
        data-testid="exercise-custom-input"
        className="input-omni mb-5"
        placeholder="e.g., Cable Crossover"
        value={customExercise}
        onChange={(e) => {
          setCustomExercise(e.target.value);
          if (e.target.value) setChosen(null);
        }}
      />

      {/* Sets / Reps / Weight */}
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-1">
            Sets
          </label>
          <input
            data-testid="exercise-sets"
            type="text"
            inputMode="numeric"
            className="input-omni"
            placeholder="4"
            value={sets}
            onChange={(e) => setSets(e.target.value.replace(/\D/g, ""))}
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-1">
            Reps
          </label>
          <input
            data-testid="exercise-reps"
            type="text"
            inputMode="numeric"
            className="input-omni"
            placeholder="8"
            value={reps}
            onChange={(e) => setReps(e.target.value.replace(/\D/g, ""))}
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-1">
            Weight
          </label>
          <div className="flex">
            <input
              data-testid="exercise-weight"
              type="text"
              inputMode="decimal"
              className="input-omni rounded-r-none"
              placeholder="185"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
            />
            <button
              data-testid="exercise-unit"
              onClick={() => setUnit(unit === "lb" ? "kg" : "lb")}
              className="bg-[#D4AF37] text-black px-3 font-bold text-xs uppercase"
              title="Toggle unit"
            >
              {unit}
            </button>
          </div>
        </div>
      </div>

      <button
        data-testid="exercise-add-btn"
        onClick={submit}
        disabled={!chosen && !customExercise.trim()}
        className="btn-omni btn-red w-full disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Plus size={16} /> Log Set
      </button>

      {/* Optional: duration + avg BPM */}
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-1">
            Duration (min)
          </label>
          <input
            data-testid="exercise-duration"
            type="text"
            inputMode="numeric"
            className="input-omni"
            placeholder="45"
            value={duration}
            onChange={(e) => setDuration(e.target.value.replace(/\D/g, ""))}
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-1">
            Avg BPM
          </label>
          <input
            data-testid="exercise-bpm"
            type="text"
            inputMode="numeric"
            className="input-omni"
            placeholder="135"
            value={bpm}
            onChange={(e) => setBpm(e.target.value.replace(/\D/g, ""))}
          />
        </div>
      </div>

      <div className="mt-4 text-xs text-neutral-500 leading-relaxed flex items-start gap-2">
        <Dumbbell size={14} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
        <span>
          Each tap = one set. Log multiple sets to build the heat-map. Sets/reps/weight optional.
        </span>
      </div>
    </div>
  );
}

/* ---------------- CardioPanel ---------------- */
function CardioPanel({ todayCardio, cardioWeek, onAdd, onRemove, onClearToday }) {
  const [cardioType, setCardioType] = useState("run");
  const [duration, setDuration] = useState("");
  const [distance, setDistance] = useState("");
  const [distanceUnit, setDistanceUnit] = useState("mi");
  const [steps, setSteps] = useState("");
  const [bpm, setBpm] = useState("");
  const [calories, setCalories] = useState("");

  const selected = CARDIO_TYPES.find((c) => c.key === cardioType) || CARDIO_TYPES[0];

  const submit = () => {
    if (!duration) return;
    onAdd({
      cardio_type: selected.key,
      label: selected.label,
      duration_min: duration,
      distance: selected.hasDistance ? distance : null,
      distance_unit: distanceUnit,
      steps,
      avg_bpm: bpm,
      calories,
    });
    setDuration("");
    setDistance("");
    setSteps("");
    setBpm("");
    setCalories("");
  };

  return (
    <div className="grid lg:grid-cols-12 gap-6" data-testid="cardio-panel">
      {/* Cardio entry form */}
      <div className="lg:col-span-7 bg-[#0F0F0F] border border-zinc-800 p-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-2">
          Log Cardio
        </div>
        <h3 className="font-display text-3xl text-white leading-tight mb-5">
          Move The <span className="text-[#DC2626]">Heart.</span>
        </h3>

        {/* Type picker */}
        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold mb-2">
          Activity
        </div>
        <div className="flex flex-wrap gap-2 mb-5">
          {CARDIO_TYPES.map((c) => (
            <button
              key={c.key}
              data-testid={`cardio-type-${c.key}`}
              onClick={() => setCardioType(c.key)}
              className={`px-3 py-2 text-xs font-semibold border-2 transition-colors flex items-center gap-2 ${
                cardioType === c.key
                  ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white"
                  : "border-zinc-800 text-neutral-300 hover:border-zinc-600"
              }`}
            >
              <span>{c.icon}</span>
              {c.label}
            </button>
          ))}
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-1 flex items-center gap-1">
              <Timer size={10} /> Duration (min)
            </label>
            <input
              data-testid="cardio-duration"
              type="text"
              inputMode="numeric"
              className="input-omni"
              placeholder="30"
              value={duration}
              onChange={(e) => setDuration(e.target.value.replace(/\D/g, ""))}
            />
          </div>
          {selected.hasDistance && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-1">
                Distance
              </label>
              <div className="flex">
                <input
                  data-testid="cardio-distance"
                  type="text"
                  inputMode="decimal"
                  className="input-omni rounded-r-none"
                  placeholder="3.1"
                  value={distance}
                  onChange={(e) => setDistance(e.target.value.replace(/[^\d.]/g, ""))}
                />
                <button
                  data-testid="cardio-distance-unit"
                  onClick={() => setDistanceUnit(distanceUnit === "mi" ? "km" : "mi")}
                  className="bg-[#D4AF37] text-black px-3 font-bold text-xs uppercase"
                  title="Toggle unit"
                >
                  {distanceUnit}
                </button>
              </div>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-1 flex items-center gap-1">
              <Heart size={10} className="text-[#DC2626]" /> Avg BPM
            </label>
            <input
              data-testid="cardio-bpm"
              type="text"
              inputMode="numeric"
              className="input-omni"
              placeholder="145"
              value={bpm}
              onChange={(e) => setBpm(e.target.value.replace(/\D/g, ""))}
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-1 flex items-center gap-1">
              <Footprints size={10} /> Steps
            </label>
            <input
              data-testid="cardio-steps"
              type="text"
              inputMode="numeric"
              className="input-omni"
              placeholder="5200"
              value={steps}
              onChange={(e) => setSteps(e.target.value.replace(/\D/g, ""))}
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-1 flex items-center gap-1">
              <Flame size={10} className="text-[#D4AF37]" /> Calories
            </label>
            <input
              data-testid="cardio-calories"
              type="text"
              inputMode="numeric"
              className="input-omni"
              placeholder="320"
              value={calories}
              onChange={(e) => setCalories(e.target.value.replace(/\D/g, ""))}
            />
          </div>
        </div>

        <button
          data-testid="cardio-add-btn"
          onClick={submit}
          disabled={!duration}
          className="btn-omni btn-red w-full disabled:opacity-50 disabled:cursor-not-allowed mt-3"
        >
          <Plus size={16} /> Log Cardio Session
        </button>

        <div className="mt-4 text-xs text-neutral-500 leading-relaxed flex items-start gap-2">
          <Heart size={14} className="text-[#DC2626] flex-shrink-0 mt-0.5" />
          <span>
            Track every form of conditioning — runs, rows, HIIT, walks. Duration is required, everything else is optional.
          </span>
        </div>
      </div>

      {/* Today's cardio + weekly totals */}
      <div className="lg:col-span-5 bg-[#0F0F0F] border border-zinc-800 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
              Today's Cardio
            </div>
            <div className="font-display text-2xl text-white">
              {todayCardio.length} {todayCardio.length === 1 ? "Session" : "Sessions"}
            </div>
          </div>
        </div>

        {/* Weekly totals */}
        <div className="grid grid-cols-4 gap-2 mb-5">
          <div className="bg-[#0A0A0A] border border-zinc-800 p-2 text-center">
            <div className="text-[9px] uppercase tracking-widest text-neutral-500">Time</div>
            <div className="font-display text-lg text-[#D4AF37]">{cardioWeek.totalMin}<span className="text-[10px] text-neutral-500">m</span></div>
          </div>
          <div className="bg-[#0A0A0A] border border-zinc-800 p-2 text-center">
            <div className="text-[9px] uppercase tracking-widest text-neutral-500">Dist</div>
            <div className="font-display text-lg text-[#D4AF37]">{cardioWeek.totalDist.toFixed(1)}</div>
          </div>
          <div className="bg-[#0A0A0A] border border-zinc-800 p-2 text-center">
            <div className="text-[9px] uppercase tracking-widest text-neutral-500">Steps</div>
            <div className="font-display text-lg text-[#D4AF37]">{cardioWeek.totalSteps.toLocaleString()}</div>
          </div>
          <div className="bg-[#0A0A0A] border border-zinc-800 p-2 text-center">
            <div className="text-[9px] uppercase tracking-widest text-neutral-500">Cal</div>
            <div className="font-display text-lg text-[#D4AF37]">{cardioWeek.totalCal}</div>
          </div>
        </div>

        <div className="space-y-2 mb-4 max-h-[360px] overflow-y-auto pr-1">
          {todayCardio.length === 0 ? (
            <p className="text-neutral-500 text-sm py-8 text-center">
              No cardio logged today. Log a session to start your heart streak.
            </p>
          ) : (
            todayCardio.map((e) => (
              <div
                key={e.id}
                data-testid={`cardio-entry-${e.id}`}
                className="bg-[#0A0A0A] border border-zinc-800 p-3 group"
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="font-display text-sm text-white leading-tight">
                    {(CARDIO_TYPES.find((c) => c.key === e.cardio_type)?.icon) || "❤️"} {e.name || e.cardio_type}
                  </div>
                  <button
                    data-testid={`cardio-remove-${e.id}`}
                    onClick={() => onRemove(e.id)}
                    className="text-neutral-600 hover:text-[#DC2626] opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Remove entry"
                  >
                    <X size={14} />
                  </button>
                </div>
                <div className="flex flex-wrap gap-3 text-[11px] text-neutral-400 mt-1">
                  {e.duration_min ? <span className="flex items-center gap-1"><Timer size={10} className="text-[#D4AF37]" />{e.duration_min}m</span> : null}
                  {e.distance ? <span>{e.distance} {e.distance_unit || "mi"}</span> : null}
                  {e.avg_bpm ? <span className="flex items-center gap-1"><Heart size={10} className="text-[#DC2626]" />{e.avg_bpm}</span> : null}
                  {e.steps ? <span className="flex items-center gap-1"><Footprints size={10} />{Number(e.steps).toLocaleString()}</span> : null}
                  {e.calories ? <span className="flex items-center gap-1"><Flame size={10} className="text-[#D4AF37]" />{e.calories}</span> : null}
                </div>
              </div>
            ))
          )}
        </div>

        <button
          data-testid="cardio-clear-today"
          onClick={onClearToday}
          disabled={todayCardio.length === 0}
          className="w-full text-xs uppercase tracking-widest font-bold text-neutral-400 hover:text-[#DC2626] py-2 border border-zinc-800 hover:border-[#DC2626] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <Trash2 size={12} className="inline mr-2" />
          Clear Today
        </button>
      </div>
    </div>
  );
}
